import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

/**
 * Settings Context
 * 
 * Manages global application settings as key-value pairs
 * Persists to localStorage for persistence across sessions
 * Accessible throughout the entire application
 */

export interface Setting {
  id: string;
  key: string;
  value: string;
  createdAt: number;
}

interface SettingsContextType {
  settings: Setting[];
  addSetting: (key: string, value: string) => void;
  removeSetting: (id: string) => void;
  updateSetting: (id: string, key: string, value: string) => void;
  getSetting: (key: string) => string | undefined;
  getAllSettings: () => Record<string, string>;
  clearAllSettings: () => void;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

const STORAGE_KEY = 'app_settings';

/**
 * Get default settings
 */
const getDefaultSettings = (): Setting[] => [
  { id: '1', key: 'appName', value: 'Aahana SmartTutor', createdAt: Date.now() },
  { id: '2', key: 'appVersion', value: '1.0.0', createdAt: Date.now() },
  { id: '3', key: 'theme', value: 'light', createdAt: Date.now() },
  { id: '4', key: 'language', value: 'en', createdAt: Date.now() },
  { id: '5', key: 'apiTimeout', value: '30000', createdAt: Date.now() },
];

/**
 * Settings Provider Component
 */
export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<Setting[]>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : getDefaultSettings();
    } catch (error) {
      console.error('Failed to load settings from localStorage:', error);
      return getDefaultSettings();
    }
  });

  // Persist settings to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch (error) {
      console.error('Failed to save settings to localStorage:', error);
    }
  }, [settings]);

  const addSetting = useCallback((key: string, value: string) => {
    const newSetting: Setting = {
      id: Date.now().toString(),
      key,
      value,
      createdAt: Date.now(),
    };

    setSettings((prev) => [...prev, newSetting]);
  }, []);

  const removeSetting = useCallback((id: string) => {
    setSettings((prev) => prev.filter((setting) => setting.id !== id));
  }, []);

  const updateSetting = useCallback((id: string, key: string, value: string) => {
    setSettings((prev) =>
      prev.map((setting) =>
        setting.id === id ? { ...setting, key, value } : setting
      )
    );
  }, []);

  const getSetting = useCallback(
    (key: string) => {
      const setting = settings.find((s) => s.key === key);
      return setting?.value;
    },
    [settings]
  );

  const getAllSettings = useCallback(() => {
    return settings.reduce(
      (acc, setting) => {
        acc[setting.key] = setting.value;
        return acc;
      },
      {} as Record<string, string>
    );
  }, [settings]);

  const clearAllSettings = useCallback(() => {
    setSettings(getDefaultSettings());
  }, []);

  const value: SettingsContextType = {
    settings,
    addSetting,
    removeSetting,
    updateSetting,
    getSetting,
    getAllSettings,
    clearAllSettings,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

/**
 * Hook to use Settings Context
 */
export const useSettings = (): SettingsContextType => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};
